import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BodyL, BrandL } from '@sorare/core/src/atoms/typography/index.tsx';
import { Avatar } from '@sorare/core/src/components/user/Avatar/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import type { RewardUnlockedStory_reward } from '#src/components/missions/stories/RewardUnlockedStory/__generated__/index.graphql.ts';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import {
  ButtonsWrapper,
  Root,
} from '#src/components/missions/stories/RewardUnlockedStory/styles.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type { ReferrerReward_inviteTaskAsReferrer } from './__generated__/index.graphql.ts';

const LogoWrapper = styled.div`
  position: absolute;
  top: calc(6 * var(--unit));
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const Content = styled(Vertical).attrs({ center: true, gap: 3 })`
  h1 {
    text-wrap: balance;
  }
`;

const Name = styled.span`
  color: var(--c-set-surface-primary);
`;

type Props = {
  onClose: () => void;
  task: ReferrerReward_inviteTaskAsReferrer;
};

export const ReferrerReward = ({ task, onClose }: Props) => {
  const { sport } = useSportContext();
  const { visual, background } = useCommonGameSet();
  const [claimTask, { loading: claiming }] = useClaimTask();
  const [claimedRewards, setClaimedRewards] = useState<
    RewardUnlockedStory_reward[]
  >([]);
  const [claimedStep, setClaimedStep] = useState<
    ReferrerReward_inviteTaskAsReferrer['steps'][number] | null
  >(null);

  const completedStep = task.steps?.find(
    step => step.aasmState === ManagerProgressionTaskState.COMPLETED
  );

  if (claimedStep) {
    return (
      <RewardUnlockedStory
        onClose={onClose}
        rewards={claimedRewards}
        fallbackRewardConfigs={claimedStep.rewardConfigs || []}
        useSetBackground
      />
    );
  }

  if (!completedStep) {
    return null;
  }

  const { referee } = task.referral;

  const onClaim = async () => {
    const { data, success } = await claimTask({
      variables: { taskId: completedStep.id, sport },
    });
    if (success) {
      setClaimedRewards(data?.claimTask?.rewards || []);
      setClaimedStep(completedStep);
    }
  };

  const { SetLogoImg } = visual.brand;

  return (
    <Root
      className="hideBackground"
      style={visual.referral?.referrerJoinedBackground || background}
    >
      {!!SetLogoImg && (
        <LogoWrapper>
          <SetLogoImg height={32} color="var(--c-white)" />
        </LogoWrapper>
      )}
      <Content>
        <Avatar user={referee} size={10} rounded />
        <Vertical center gap={2}>
          <BrandL as="h1" className="text-center" color="var(--c-white)">
            <FormattedMessage
              defaultMessage="<green>{name}</green> joined Sorare with your invite!"
              values={{
                name: referee.nickname,
                green: chunks => <Name>{chunks}</Name>,
              }}
            />
          </BrandL>
          <BodyL className="text-center" color="var(--c-white)">
            <FormattedMessage defaultMessage="You can now follow their path and earn rewards!" />
          </BodyL>
        </Vertical>
      </Content>
      <ButtonsWrapper>
        <Button
          size="large"
          color="primary"
          onClick={onClaim}
          disabled={claiming}
        >
          <FormattedMessage defaultMessage="Claim my reward" />
        </Button>
      </ButtonsWrapper>
    </Root>
  );
};

ReferrerReward.fragments = {
  inviteTaskAsReferrer: gql`
    fragment ReferrerReward_inviteTaskAsReferrer on InviteTaskAsReferrer {
      id
      steps {
        id
        aasmState
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
      referral {
        id
        referee {
          slug
          nickname
          ...Avatar_user
        }
      }
    }
    ${Avatar.fragments.user}
    ${RewardUnlockedStory.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ReferrerReward_inviteTaskAsReferrer>,
};
